<script setup>
import { ref, watch, computed } from "vue";
import Swal from "sweetalert2";
import FileIcon from "@/assets/img/download-01.png";

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    documents: {
        type: Array,
        default: () => [],
    },
    selectedDocumentIds: {
        type: [Array, Set],
        default: () => [],
    },
    projectId: {
        type: [Number, String, null],
        default: null,
    },
});

const emit = defineEmits(["update:visible", "downloaded"]);

const showFileSelectionModal = ref(false);
const selectedDocumentsForDownload = ref([]);
const documentFileSelections = ref(new Map());
const isFileSelectionDownloading = ref(false);

const normalizedSelectedIds = computed(() => {
    if (props.selectedDocumentIds instanceof Set) {
        return Array.from(props.selectedDocumentIds);
    }
    return Array.isArray(props.selectedDocumentIds)
        ? props.selectedDocumentIds
        : [];
});

watch(
    () => props.visible,
    (val) => {
        showFileSelectionModal.value = val;
        if (val) {
            const ids = normalizedSelectedIds.value;
            if (ids.length) {
                handleDownloadSelectedFiles(ids);
            }
        }
    },
    { immediate: true },
);

watch(showFileSelectionModal, (val) => {
    if (!val) {
        emit("update:visible", false);
    }
});

function handleDownloadSelectedFiles(documentIds) {
    if (!documentIds.length) return;

    showFileSelectionModal.value = true;
    selectedDocumentsForDownload.value = documentIds;
    initializeFileSelections(documentIds);
}

function getFileIdentifier(file) {
    // Use a consistent method to identify files across the component
    return file.id || file.file_id || file.name || file.original_name || file.file_name || file.filename || JSON.stringify(file);
}

function initializeFileSelections(documentIds) {
    documentFileSelections.value.clear();

    documentIds.forEach((docId) => {
        const document = props.documents.find((d) => d.id === docId);
        const files = getDocumentFiles(document); // Updated

        if (files.length > 0) {
            const fileIds = new Set(files.map((file) => getFileIdentifier(file)));
            documentFileSelections.value.set(docId, fileIds);
            console.log(`Initialized ${fileIds.size} files for document ${docId}:`, Array.from(fileIds));
        }
    });
}

function getDocumentFiles(document) {
    if (!document) return [];

    // Check every possible place the backend might store files
    const candidates = [
        document.files,
        document.document_files,
        document.documentFiles,
        document.attachments,
        document.versions,
        document.latestVersion?.files,
        document?.data?.files,
        document.file ? [document.file] : null, // Handle singular file object
        document.media
    ];

    for (let c of candidates) {
        if (!c) continue;
        
        // 1. Strip Fractal '.data' wrapper if the backend uses it
        if (c.data && Array.isArray(c.data)) c = c.data;
        
        // 2. Standard Array check
        if (Array.isArray(c) && c.length > 0) return c;
        
        // 3. Object-casted array check (happens if JSON encodes an array as an object)
        if (typeof c === 'object' && !Array.isArray(c) && Object.keys(c).length > 0) {
            const vals = Object.values(c);
            if (vals[0] && (vals[0].id || vals[0].file_id || vals[0].name)) return vals;
        }
    }

    // If it reaches here, the API truly isn't sending file data
    console.warn("No files found in document payload:", document);
    return [];
}

function getDocumentName(docId) {
    const document = props.documents.find((d) => d.id === docId);
    return document?.name || document?.title || "Untitled";
}

function toggleAllFilesInDocument(docId) {
    const document = props.documents.find((d) => d.id === docId);
    const files = getDocumentFiles(document); // Updated
    if (!files.length) return;

    const currentSelection = documentFileSelections.value.get(docId) || new Set();
    const allFileIds = new Set(files.map((file) => getFileIdentifier(file)));

    if (currentSelection.size === allFileIds.size) {
        documentFileSelections.value.set(docId, new Set());
    } else {
        documentFileSelections.value.set(docId, allFileIds);
    }
}

function toggleFileInDocument(docId, fileId) {
    const currentSelection =
        documentFileSelections.value.get(docId) || new Set();
    const normalizedFileId = getFileIdentifier({ id: fileId, name: fileId });

    if (currentSelection.has(normalizedFileId)) {
        currentSelection.delete(normalizedFileId);
    } else {
        currentSelection.add(normalizedFileId);
    }

    documentFileSelections.value.set(docId, currentSelection);
}

function isAllFilesSelectedInDocument(docId) {
    const document = props.documents.find((d) => d.id === docId);
    const files = getDocumentFiles(document); // Updated
    if (!files.length) return false;

    const currentSelection = documentFileSelections.value.get(docId) || new Set();
    const allFileIds = new Set(files.map((file) => getFileIdentifier(file)));

    return currentSelection.size === allFileIds.size && allFileIds.size > 0;
}

function isFileSelectedInDocument(docId, fileId) {
    const currentSelection =
        documentFileSelections.value.get(docId) || new Set();
    const normalizedFileId = getFileIdentifier({ id: fileId, name: fileId });
    return currentSelection.has(normalizedFileId);
}

function getSelectedFilesCount(docId) {
    const currentSelection =
        documentFileSelections.value.get(docId) || new Set();
    return currentSelection.size;
}

function getTotalFilesCount(docId) {
    const document = props.documents.find((d) => d.id === docId);
    return getDocumentFiles(document).length; // Updated
}

function confirmFileSelectionDownload() {
    let totalSelectedFiles = 0;
    let totalFiles = 0;

    console.log('Document file selections:', documentFileSelections.value);

    documentFileSelections.value.forEach((selectedFiles, docId) => {
        totalSelectedFiles += selectedFiles.size;
        totalFiles += getTotalFilesCount(docId);
        console.log(`Document ${docId}: ${selectedFiles.size} files selected, ${getTotalFilesCount(docId)} total files`);
    });

    console.log(`Total selected files: ${totalSelectedFiles}, Total files: ${totalFiles}`);

    if (totalSelectedFiles === 0) {
        Swal.fire({
            title: "No Files Selected",
            text: "Please select at least one file to download.",
            icon: "warning",
            confirmButtonText: "OK",
        });
        return;
    }

    Swal.fire({
        title: "Confirm Download",
        html: `
      <div class="custom-icon">
        <img src="${FileIcon}" alt="File Icon" style="width: 96px; height: 96px;">
      </div>
      <div class="confirmation-message">
        Download ${totalSelectedFiles} selected file${totalSelectedFiles > 1 ? "s" : ""
            } from ${selectedDocumentsForDownload.value.length} document${selectedDocumentsForDownload.value.length > 1 ? "s" : ""
            }?
      </div>
      <div class="confirmation-question">
        Are you sure?
      </div>
    `,
        showCancelButton: true,
        confirmButtonText: "Download",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        customClass: {
            popup: "custom-confirmation-dialog download-dialog",
            container: "swal-high-z-index",
        },
    }).then((result) => {
        if (result.isConfirmed) {
            performFileSelectionDownload();
        }
    });
}

async function performFileSelectionDownload() {
    isFileSelectionDownloading.value = true;

    const downloadData = {
        project_id: props.projectId || null,
        documents: selectedDocumentsForDownload.value.map((docId) => ({
            document_id: docId,
            file_ids: Array.from(
                documentFileSelections.value.get(docId) || [],
            ),
        })),
    };

    Swal.fire({
        title: "Starting Download",
        text: `Preparing selected files for download...`,
        icon: "info",
        position: "top-end",
        showConfirmButton: false,
        timer: 8000,
        toast: true,
    });

    const url = "/api/v2/docs/bulk-download";

    try {
        const tokenMeta = document.querySelector('meta[name="csrf-token"]');
        const metaToken = tokenMeta ? tokenMeta.getAttribute("content") : "";
        const getCookie = (name) => {
            const match = document.cookie.match(
                new RegExp(
                    "(^|; )" +
                    name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") +
                    "=([^;]*)",
                ),
            );
            return match ? decodeURIComponent(match[2]) : "";
        };
        let xsrf = getCookie("XSRF-TOKEN");
        if (!xsrf && !metaToken) {
            try {
                await fetch("/sanctum/csrf-cookie", { credentials: "include" });
                xsrf = getCookie("XSRF-TOKEN");
            } catch (_) { }
        }

        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/zip",
                "X-Requested-With": "XMLHttpRequest",
                ...(metaToken ? { "X-CSRF-TOKEN": metaToken } : {}),
                ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
            },
            credentials: "include",
            body: JSON.stringify(downloadData),
        });

        if (!res.ok) {
            if (res.status === 419) {
                try {
                    await fetch("/sanctum/csrf-cookie", {
                        credentials: "include",
                    });
                    const retry = await fetch(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/zip",
                            "X-Requested-With": "XMLHttpRequest",
                            ...(metaToken ? { "X-CSRF-TOKEN": metaToken } : {}),
                            "X-XSRF-TOKEN": getCookie("XSRF-TOKEN") || "",
                        },
                        credentials: "include",
                        body: JSON.stringify(downloadData),
                    });
                    if (!retry.ok) throw new Error(await retry.text());
                    return await handleZipStreamResponse(retry);
                } catch (e) {
                    throw new Error(
                        "Session expired. Please refresh and try again.",
                    );
                }
            }
            let msg = "Unable to download selected files.";
            try {
                const data = await res.json();
                msg = data?.message || msg;
            } catch (_) { }
            throw new Error(msg);
        }

        await handleZipStreamResponse(res);
        emit("downloaded");
    } catch (err) {
        console.error("Bulk download failed", err);

        Swal.fire({
            title: "Download Failed",
            text:
                err?.message ||
                "Unable to download selected documents.",
            icon: "error",
            position: "top-end",
            showConfirmButton: false,
            timer: 5000,
            toast: true,
        });
    } finally {
        isFileSelectionDownloading.value = false;
    }
}

async function handleZipStreamResponse(res) {
    const blob = await res.blob();
    const link = document.createElement("a");
    const cd = res.headers.get("content-disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/);
    const filename = match
        ? match[1]
        : props.projectId
            ? `project_${props.projectId}_documents_${Date.now()}.zip`
            : `selected_documents_${Date.now()}.zip`;
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);

    Swal.fire({
        title: "Download Complete",
        text: "Your documents have been downloaded successfully!",
        icon: "success",
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
        toast: true,
    });

    showFileSelectionModal.value = false;
    emit("update:visible", false);
}
</script>

<template>
    <el-dialog v-model="showFileSelectionModal" width="800px" class="file-selection-modal" :show-close="true"
        destroy-on-close>
        <template #header>
            <div class="d-flex justify-content-between align-items-center w-100">
                <div class="h5 m-0">Select Files to Download</div>
            </div>
        </template>

        <div class="file-selection-content">
            <div class="selection-summary">
                <div class="alert alert-info alert-info-file-selection">
                    <strong>{{ selectedDocumentsForDownload.length }}</strong>
                    document{{
                        selectedDocumentsForDownload.length > 1 ? "s" : ""
                    }}
                    selected
                </div>
            </div>

            <div class="documents-file-selection">
                <div v-for="docId in selectedDocumentsForDownload" :key="docId" class="document-collapsible mb-3">
                    <div class="document-header" @click="toggleAllFilesInDocument(docId)">
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <div class="d-flex align-items-center gap-3">
                                <div class="document-checkbox">
                                    <input type="checkbox" class="form-check-input"
                                        :checked="isAllFilesSelectedInDocument(docId)"
                                        @change.stop="toggleAllFilesInDocument(docId)" />
                                </div>
                                <div class="document-title">
                                    {{ getDocumentName(docId) }}
                                </div>
                            </div>
                            <div class="files-count">
                                {{ getSelectedFilesCount(docId) }} / {{
                                    getTotalFilesCount(docId)
                                }}
                                files selected
                            </div>
                        </div>
                    </div>

                    <div class="document-files">
                        <div v-for="file in getDocumentFiles(documents.find((d) => d.id === docId))"
                            :key="getFileIdentifier(file)" class="file-item">
                            <input type="checkbox" class="form-check-input me-2" :checked="isFileSelectedInDocument(
                                docId,
                                getFileIdentifier(file),
                            )" @change.stop="
                                    toggleFileInDocument(
                                        docId,
                                        getFileIdentifier(file),
                                    )
                                    " />
                            <span>{{ file.name || file.original_name || file.file_name || file.filename }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="file-selection-actions mt-3 text-end">
                <button class="btn btn-secondary me-2" type="button" @click="showFileSelectionModal = false">
                    Cancel
                </button>
                <button class="btn btn-primary" type="button" :disabled="isFileSelectionDownloading"
                    @click="confirmFileSelectionDownload">
                    <span v-if="isFileSelectionDownloading" class="spinner-border spinner-border-sm me-2"
                        role="status" />
                    <span>
                        {{
                            isFileSelectionDownloading
                                ? "Preparing Download..."
                                : "Download Selected Files"
                        }}
                    </span>
                </button>
            </div>
        </div>
    </el-dialog>
</template>

<style scoped>
.file-selection-modal {
    z-index: 3000;
}

.alert-info-file-selection {
    margin-bottom: 16px;
}

.documents-file-selection {
    max-height: 400px;
    overflow-y: auto;
}

.document-collapsible {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
}

.document-header {
    cursor: pointer;
}

.document-files {
    margin-top: 8px;
    padding-left: 24px;
}

.file-item {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}
</style>
